/**
 * Dev-server-only CSP relaxation for a local Supabase instance.
 *
 * index.html ships a production CSP whose `connect-src` allowlists
 * `https://*.supabase.co`. E2E (and local dev) run against a local
 * Supabase on `http://127.0.0.1:54321`. CSP origins are scheme- and
 * port-sensitive, so `'self'` (the :8080 dev server) does not cover it —
 * Chromium blocks `POST /auth/v1/token` before it leaves the page, and
 * sign-in fails with no network request at all.
 *
 * `applyDevCsp` appends the configured Supabase origin (plus its websocket
 * scheme, for realtime) to `connect-src`. It is wired into vite.config.ts
 * behind `apply: 'serve'`, so the production build never sees it.
 *
 * Kept dependency-free and separate from vite.config.ts so it is unit
 * testable without dragging esbuild into the jsdom test environment.
 */

/** Extract the bare origin, or null when the input is missing/unparseable. */
function toOrigin(url: string | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/**
 * Append `supabaseUrl`'s origin to the CSP `connect-src` of `html`.
 *
 * No-op when the URL is absent, unparseable, or already permitted by an
 * existing source. Returns `html` unchanged if there is no CSP meta tag.
 */
export function applyDevCsp(html: string, supabaseUrl: string | undefined): string {
  const origin = toOrigin(supabaseUrl);
  if (!origin) return html;

  return html.replace(/(connect-src)([^;]*)(;)/, (match, name: string, sources: string, end: string) => {
    const existing = sources.trim().split(/\s+/).filter(Boolean);

    // Already covered by an exact entry or a wildcard host entry.
    const covered = existing.some((src) => {
      if (src === origin) return true;
      if (!src.includes('*.')) return false;
      const [scheme, hostSuffix] = src.split('://*.');
      if (!hostSuffix) return false;
      const target = new URL(origin);
      return target.protocol === `${scheme}:` && target.host.endsWith(hostSuffix);
    });
    if (covered) return match;

    const wsOrigin = origin.replace(/^http/, 'ws');
    return `${name}${sources} ${origin} ${wsOrigin}${end}`;
  });
}
